import { ZodError, type ZodType, type infer as zInfer } from 'zod';

/**
 * Thrown by the axios response interceptor when a payload fails to
 * validate against the schema attached to the request config (see
 * `__zodSchema` on `AxiosRequestConfig`).
 *
 * Carries a normalised `zodIssues` list so call-sites and logging
 * pipelines can introspect failures without importing Zod's types.
 * The original `ZodError` is preserved on `.cause` for full fidelity.
 *
 * Note: the auto-generated VynaEMR schemas emit verbose English
 * messages such as `"Invalid value: Expected int64 to be >= -9223372036854775808"`.
 * These are intended for triage, NOT end-user display. The interceptor
 * shows a friendly toast and routes the raw issues to `console.error`.
 */
export class ApiResponseValidationError extends Error {
  readonly url: string;
  readonly zodIssues: ReadonlyArray<{
    path: string;
    message: string;
    code: string;
  }>;

  constructor(url: string, cause: ZodError) {
    super(`API response validation failed for ${url}`);
    this.name = 'ApiResponseValidationError';
    this.url = url;
    this.zodIssues = cause.issues.map((issue) => ({
      path: issue.path.map((p) => String(p)).join('.'),
      message: issue.message,
      code: String(issue.code ?? ''),
    }));
    this.cause = cause;
  }
}

/**
 * Run `schema.safeParse(data)` and either return the parsed value or
 * throw `ApiResponseValidationError` carrying the URL + ZodError.
 *
 * Generic over the schema's input/output. Note for the snake_case
 * generated schemas: the OUTPUT type is snake_case — callers running
 * before `keysToCamel` will see the validated snake_case payload.
 */
export function validateResponse<T extends ZodType>(
  schema: T,
  url: string,
  data: unknown,
): zInfer<T> {
  const result = schema.safeParse(data);
  if (!result.success) {
    throw new ApiResponseValidationError(url, result.error);
  }
  return result.data;
}
